import { useTranslation } from 'react-i18next'
import { TContent } from './content.type'
import { ActionsCell, ContentCell, TextCell } from './content-cells.component'
import { ReactNode, useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
  CurecallButton,
  CurecallList,
  CurecallListHeaders,
  ECurecallButtonAppearance,
  ECurecallSize,
} from '@honestica/curecall-ui'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { getContentLanguages } from './content-list.util'
import { ContentViewModal } from './content-preview-modal.component'
import {
  EList,
  getListPreferedOrder,
  setListPreferedOrder,
  TOrderBy,
} from 'src/utils/list-order-preference.util'
import { useLocalizedNavigate } from 'src/hooks/use-localized-navigate'

type TContentColumn = 'Id' | 'ContentName' | 'Languages' | 'Content' | 'Actions'
type TTableRow = { [key in TContentColumn]: ReactNode }

type TSortableColumn = Extract<TContentColumn, 'Id' | 'ContentName'>
const COLUMN_SORTS: { [key in TSortableColumn]: any } = {
  Id: (contents: TContent[]) => [...contents].sort((content1, content2) => content1.id - content2.id),
  ContentName: (contents: TContent[]) =>
    [...contents].sort((content1, content2) => content1.name.localeCompare(content2.name)),
}

export default function ContentList({ contents }: Readonly<{ contents: TContent[] }>) {
  const defaultPreferedOrder: TOrderBy<TSortableColumn> = getListPreferedOrder(EList.CONTENT_LIST) || {
    column: 'Id',
    descOrder: false,
  }

  const defaultSortedContents = defaultPreferedOrder.descOrder
    ? COLUMN_SORTS[defaultPreferedOrder.column as TSortableColumn](contents).reverse()
    : COLUMN_SORTS[defaultPreferedOrder.column as TSortableColumn](contents)

  return (
    <ContentListComponent
      defaultSortedContents={defaultSortedContents}
      defaultPreferedOrder={defaultPreferedOrder}
    />
  )
}

function ContentListComponent({
  defaultSortedContents,
  defaultPreferedOrder,
}: Readonly<{ defaultSortedContents: TContent[]; defaultPreferedOrder: TOrderBy<TSortableColumn> }>) {
  const { t } = useTranslation()
  const navigate = useLocalizedNavigate()
  const { pathname } = useLocation()
  const { project } = useCurrentProject()

  const [contentToShow, setContentToShow] = useState<TContent | null>(null)
  const [contentsSorted, setContentsSorted] = useState<TContent[]>(defaultSortedContents)
  const [currentOrder, setCurrentOrder] = useState<TOrderBy<TSortableColumn>>(defaultPreferedOrder)

  const sortTableByColumn = (columnToSort: TSortableColumn) => {
    const shouldReverseOrder = currentOrder.column === columnToSort
    const newOrder = {
      column: columnToSort,
      descOrder: shouldReverseOrder ? !currentOrder.descOrder : false,
    }
    const colonneSort = COLUMN_SORTS[columnToSort]
    setContentsSorted((prev) => (newOrder.descOrder ? colonneSort(prev).reverse() : colonneSort(prev)))
    setCurrentOrder(newOrder)
    setListPreferedOrder(EList.CONTENT_LIST, newOrder)
  }

  const tableRows: Array<TTableRow> =
    contentsSorted?.map((content: TContent) => {
      return {
        Id: <TextCell>{content.id}</TextCell>,
        ContentName: <TextCell>{content.name || content.label}</TextCell>, // when origin = SELF_CONFIGURATION content's name is defined, in other cases we take the label
        Languages: (
          <TextCell>
            {getContentLanguages(content)
              .map((language) => t('common.languages', { language }))
              .join('/')}
          </TextCell>
        ),
        Content: <ContentCell content={content} />,
        Actions: (
          <ActionsCell
            content={content}
            setContentToShow={setContentToShow}
            onUpdateContent={(contentId: number) => navigate(`${pathname}/${contentId}`)}
          />
        ),
      }
    }) ?? []

  const columnsData: CurecallListHeaders<TTableRow> = [
    {
      label: t('pages.configuration.contents.id'),
      key: 'Id',
      isSorted: true,
      onHeaderClick: () => sortTableByColumn('Id'),
    },
    {
      label: t('pages.configuration.contents.content-name'),
      key: 'ContentName',
      isSorted: true,
      onHeaderClick: () => sortTableByColumn('ContentName'),
    },
    { label: t('pages.configuration.contents.languages'), key: 'Languages' },
    { label: t('pages.configuration.contents.content'), key: 'Content' },
    {
      label: t('pages.configuration.contents.actions'),
      key: 'Actions',
      headerClassName: 'tw-pr-10 tw-flex tw-justify-end',
    },
  ]

  return (
    <div className="c-flex c-flex-col c-max-h-full">
      <header className="c-flex c-mb-6 c-items-end">
        <CurecallButton
          appearance={ECurecallButtonAppearance['PRIMARY-OUTLINED']}
          size={ECurecallSize.SMALL}
          onClick={() => navigate(`/${project.id}/configuration/contents/add`)}
          className="c-ml-auto"
        >
          {t('pages.configuration.contents.add-content')}
        </CurecallButton>
      </header>

      <CurecallList
        rowsData={tableRows}
        headersData={columnsData}
        currentOrder={{ key: currentOrder.column, descOrder: currentOrder.descOrder }}
        className="c-mb-16 [&_td]:c-align-middle"
      />

      {contentToShow && (
        <ContentViewModal contentToShow={contentToShow} setContentToShow={setContentToShow} />
      )}
    </div>
  )
}
